export type UserStatus = "online" | "calling" | "receiving" | "in-call";

export type MessageType =
  | "user-login"
  | "login-success"
  | "login-error"
  | "initiate-call"
  | "incoming-call"
  | "accept-call"
  | "reject-call"
  | "cancel-call"
  | "hang-up"
  | "call-accepted"
  | "call-rejected"
  | "call-cancelled"
  | "call-started"
  | "call-ended"
  | "call-error"
  | "offer"
  | "answer"
  | "ice-candidate"
  | "get-online-users"
  | "online-users";

export interface WebSocketMessage {
  type: MessageType;
  data: any;
}

export interface User {
  id: string;
  email: string;
  type: "normal" | "therapist";
  status: UserStatus;
  lastSeen: string;
  inCallWith?: string;
  name?: string;
  profile_photo_url?: string;
}

export interface LoginData {
  userId: string;
}

export interface CallData {
  targetUserId?: string;
  callerId?: string;
  conversationId?: string;
}

export interface WebRTCSignalData {
  targetUserId: string;
  fromUserId?: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidate;
}

export interface ErrorData {
  message: string;
}

export interface OnlineUsersData {
  users: string[];
}

export type AppScreen =
  | "login"
  | "main"
  | "main-provider"
  | "calling"
  | "incoming"
  | "in-call";

export interface WebRTCConfig {
  iceServers: RTCIceServer[];
}

export interface OnlineUsersResponse {
  users: User[];
}

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

export interface Provider {
  id: string;
  user_id?: string; // For registered therapists, this is the user.id that links to the users table
  name: string;
  avatar: string;
  title: string;
  description: string;
  type: string;
  email: string;
  profile_data?: TherapistProfileData; // For unregistered therapists from DB or JSON
  is_online?: boolean; // Whether the therapist is currently online
  is_registered?: boolean; // Whether the therapist is a registered user
}

// Therapist profile data structure (from database or JSON files)
export interface TherapistProfileData {
  name: string;
  title?: string;
  location?: string;
  specialties?: any; // Can be array or object with Top Specialties/Expertise
  phone?: string;
  self_description?: string;
  treatment_approach?: string[];
  languages?: string[];
  insurance_accepted?: string[];
  session_fee?: Record<string, string>;
  photo_urls?: string[];
  years_practicing?: string | number | null;
  education?: string[];
  profile_url?: string;
  created_at?: string; // ISO 8601 timestamp
  locations?: TherapistLocation[];
  licenses?: TherapistLicense[];
}

// Office location for a therapist
export interface TherapistLocation {
  id: string;
  address_line?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  lat?: number;
  lon?: number;
  in_person?: boolean;
  is_primary?: boolean;
}

// State licensure for a therapist
export interface TherapistLicense {
  id: string;
  state: string;
  license_type?: string;
  license_number?: string;
}

export interface ChatResponse {
  conversation_id: string;
  answer: string;
  user_list: Provider[] | null;
}

export interface ConversationResponse {
  conversation_id: string;
}

export interface SendMessageRequest {
  id: string;
  conversationId: string;
  question: string;
}

export interface ProfessionalCredentials {
  fullName: string;
  professionalTitle: string;
  licenseType: string;
  licenseNumber: string;
  licensedStates: string;
  yearsOfExperience: string;
  education: {
    school: string;
    degree: string;
    fieldOfStudy: string;
    graduationYear: string;
  };
  // Primary office location
  location: {
    city: string;
    state: string;
    postalCode: string;
    address: string; // Optional street address
  };
  // Session fees
  fees: {
    individual: string;
    couples: string;
    slidingScale: boolean;
  };
  // Insurance
  insuranceAccepted: string[]; // Array of insurance provider names
  // Professional photo
  profilePhoto: string; // URL or base64
  // Self description/bio
  selfDescription: string;
  // Contact phone number
  phone: string;
}

export type TherapyModalityName = (typeof THERAPY_MODALITIES)[number];
export type ClinicalSpecializationName =
  (typeof CLINICAL_SPECIALIZATIONS)[number];
export type ClientAgeGroupName = (typeof CLIENT_AGE_GROUPS)[number];
export type SessionTypeName = (typeof SESSION_TYPES)[number];
export type GenderPreferenceName = (typeof GENDER_PREFERENCES)[number];
export type PopulationSpecializationName =
  (typeof POPULATION_SPECIALIZATIONS)[number];

export type ItemName =
  | string
  | TherapyModality
  | ClinicalSpecialization
  | ClientAgeGroupName
  | SessionTypeName
  | GenderPreferenceName
  | PopulationSpecializationName
  | ReligiousBackground
  | CulturalBackground
  | Language
  | SexualOrientation
  | TherapeuticStyle
  | SpiritualityApproach
  | NonTreatmentIssue;

export interface CheckboxItem {
  name: ItemName;
  yearsOfExperience?: number;
  isOther?: boolean;
  otherValue?: string;
  value?: string;
}

export interface SessionTypeWithExperience extends CheckboxItem {
  name: SessionTypeName;
  yearsOfExperience: number;
}

export const RELIGIOUS_BACKGROUNDS = [
  "Prefer not to answer",
  "Christian (Protestant)",
  "Christian (Catholic)",
  "Christian (Orthodox)",
  "Jewish",
  "Muslim",
  "Hindu",
  "Buddhist",
  "Sikh",
  "Spiritual but not religious",
  "Agnostic",
  "Atheist",
] as const;

export type ReligiousBackground = (typeof RELIGIOUS_BACKGROUNDS)[number];

export const CULTURAL_BACKGROUNDS = [
  "Prefer not to answer",
  "African American/Black",
  "Asian American",
  "Latino/Hispanic",
  "Native American",
  "Middle Eastern",
  "Pacific Islander",
  "White/Caucasian",
  "Multiracial/Mixed",
] as const;

export type CulturalBackground = (typeof CULTURAL_BACKGROUNDS)[number];

export const LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Mandarin",
  "Cantonese",
  "Japanese",
  "Korean",
  "Arabic",
  "Hebrew",
  "Russian",
  "Hindi",
] as const;

export type Language = (typeof LANGUAGES)[number];

export const SEXUAL_ORIENTATIONS = [
  "Prefer not to answer",
  "Heterosexual",
  "Gay",
  "Lesbian",
  "Bisexual",
  "Pansexual",
  "Asexual",
  "Queer",
] as const;

export type SexualOrientation = (typeof SEXUAL_ORIENTATIONS)[number];

export interface PersonalBackground {
  religiousBackground?: CheckboxItem[];
  culturalBackground?: CheckboxItem[];
  languages: CheckboxItem[];
  sexualOrientation?: CheckboxItem[];
}

export const THERAPEUTIC_STYLES = [
  "Very structured/directive",
  "Moderately structured",
  "Flexible/adaptive",
  "Client-led/non-directive",
] as const;

export type TherapeuticStyle = (typeof THERAPEUTIC_STYLES)[number];

export const SPIRITUALITY_APPROACHES = [
  "Actively incorporate when appropriate",
  "Open to discussing if client brings it up",
  "Neutral/secular approach only",
  "Prefer not to incorporate",
] as const;

export type SpiritualityApproach = (typeof SPIRITUALITY_APPROACHES)[number];

export const NON_TREATMENT_ISSUES = [
  "Severe mental illness (schizophrenia, severe bipolar)",
  "Active suicidal ideation",
  "Active substance abuse",
  "Domestic violence (perpetrators)",
  "Sexual offenses",
  "Sex-related issues (e.g. sex addiction)",
  "Eating disorders",
  "Children under 10",
  "Couples therapy",
  "Group therapy",
  "Court-mandated clients",
] as const;

export type NonTreatmentIssue = (typeof NON_TREATMENT_ISSUES)[number];

export interface PracticeApproach {
  therapeuticStyle: CheckboxItem[];
  spiritualityApproach: CheckboxItem[];
  nonTreatmentIssues: CheckboxItem[];
}

export const INSURANCE_PROVIDERS = [
  "Aetna",
  "Anthem",
  "Blue Cross Blue Shield",
  "Cigna",
  "United Healthcare",
  "Medicare",
  "Medicaid",
  "Humana",
  "Kaiser Permanente",
  "Tricare",
  "Out of network",
] as const;

export type InsuranceProvider = (typeof INSURANCE_PROVIDERS)[number];

export const PROFESSIONAL_MEMBERSHIPS = [
  "American Psychological Association (APA)",
  "National Association of Social Workers (NASW)",
  "American Association for Marriage and Family Therapy (AAMFT)",
  "American Counseling Association (ACA)",
] as const;

export type ProfessionalMembership = (typeof PROFESSIONAL_MEMBERSHIPS)[number];

export interface AdditionalInformation {
  specialCertifications: string;
  professionalMemberships: CheckboxItem[];
  additionalNotes: string;
}

export interface QuestionnaireData {
  professionalCredentials: ProfessionalCredentials;
  therapeuticSpecializations: {
    topModalities: TherapyModality[];
    allTrainedModalities: TherapyModality[];
    topSpecializations: ClinicalSpecialization[];
    allClinicalAreas: ClinicalSpecialization[];
  };
  clientPreferences: {
    ageGroups: CheckboxItem[];
    sessionTypes: {
      primary: SessionTypeWithExperience[];
      allTrained: CheckboxItem[];
    };
    genderPreferences: CheckboxItem[];
    populationSpecializations: CheckboxItem[];
  };
  personalBackground: PersonalBackground;
  practiceApproach: PracticeApproach;
  additionalInformation: AdditionalInformation;
}

export interface QuestionAnswer {
  subtitle: string;
  answer: string;
}

export interface QuestionData {
  title: string;
  type: "text" | "choice";
  answer?: QuestionAnswer[];
  choices?: string[];
}

export const CLIENT_AGE_GROUPS = [
  "Children (5-12 years)",
  "Adolescents (13-17 years)",
  "Young Adults (18-25 years)",
  "Adults (26-64 years)",
  "Seniors (65+ years)",
] as const;

export type ClientAgeGroup = (typeof CLIENT_AGE_GROUPS)[number];

export const SESSION_TYPES = [
  "Individual Therapy",
  "Couples Therapy",
  "Family Therapy",
  "Group Therapy",
  "Intensive Outpatient Programs (IOP)",
  "Support Groups",
  "Psychoeducational Groups",
  "Skills-Based Groups",
] as const;

export type SessionType = (typeof SESSION_TYPES)[number];

export const GENDER_PREFERENCES = [
  "No preference",
  "Prefer working with women",
  "Prefer working with men",
  "Prefer working with non-binary individuals",
] as const;

export type GenderPreference = (typeof GENDER_PREFERENCES)[number];

export const POPULATION_SPECIALIZATIONS = [
  "LGBTQ+ individuals",
  "Veterans/Military families",
  "Healthcare workers",
  "First responders",
  "College students",
  "Parents/Caregivers",
  "Divorced/Separated individuals",
  "Chronic illness/disability",
  "Cultural minorities",
  "Religious/Spiritual individuals",
  "Athletes/Performers",
  "Business professionals",
  "No specific population focus",
] as const;

export type PopulationSpecialization =
  (typeof POPULATION_SPECIALIZATIONS)[number];

export interface ClientPreferences {
  ageGroups: ClientAgeGroup[];
  sessionTypes: {
    primary: SessionType[];
    allTrained: SessionType[];
  };
  genderPreferences: (GenderPreference | { isOther: true; value: string })[];
  populationSpecializations: PopulationSpecialization[];
}

export interface TherapyModality extends CheckboxItem {
  name: (typeof THERAPY_MODALITIES)[number];
}

export interface ClinicalSpecialization extends CheckboxItem {
  name: (typeof CLINICAL_SPECIALIZATIONS)[number];
}

export interface TherapeuticSpecializations {
  topModalities: TherapyModality[];
  allTrainedModalities: TherapyModality[];
  topSpecializations: ClinicalSpecialization[];
  allClinicalAreas: ClinicalSpecialization[];
}

export const THERAPY_MODALITIES = [
  "Cognitive Behavioral Therapy (CBT)",
  "Dialectical Behavior Therapy (DBT)",
  "Eye Movement Desensitization and Reprocessing (EMDR)",
  "Acceptance and Commitment Therapy (ACT)",
  "Psychodynamic/Psychoanalytic",
  "Humanistic/Person-Centered",
  "Family Systems Therapy",
  "Solution-Focused Brief Therapy",
  "Mindfulness-Based Therapy",
  "Gestalt Therapy",
  "Narrative Therapy",
  "Interpersonal Therapy (IPT)",
  "Motivational Interviewing",
  "Play Therapy",
  "Art/Expressive Therapy",
  "Somatic Therapy",
  "Cognitive Processing Therapy (CPT)",
] as const;

export const CLINICAL_SPECIALIZATIONS = [
  "Anxiety Disorders",
  "Depression",
  "Trauma/PTSD",
  "Addiction/Substance Abuse",
  "Eating Disorders",
  "Relationship/Couples Issues",
  "Family Therapy",
  "Grief/Loss",
  "Life Transitions",
  "Anger Management",
  "Bipolar Disorder",
  "Personality Disorders",
  "OCD",
  "ADHD",
  "Autism Spectrum",
  "Career Counseling",
  "Sleep Disorders",
  "Chronic Pain/Illness",
  "Sexual Issues",
  "Phobias/Panic Disorders",
  "Social Anxiety",
  "Body Image Issues",
  "Stress Management",
  "Self-Esteem Issues",
] as const;

export interface UserBackground {
  title: string;
  type: "text" | "choice";
  choices?: string[];
  answer?: {
    subtitle: string;
    answer: string;
  }[];
}
